import { domainRegionMap } from '@gap/utils/region-utils'
import { isProdLikeStage } from '@gap/utils/stage'
import { namingConfig } from '@gap/utils/config/naming-config'
import { extractUrlRegion } from "reusable/utils/url-utils"
import { VimeoUrl } from '../app-services/vimeo/VimeoService'
import { UserDto } from '../app-services/user/models/UserDto'

// This module is written for Vite's `import.meta.env` (browser bundle or `vite dev`), which always
// defines that object. Some pre-rewrite Playwright fixtures (see PlaywrightContext.ts) still import
// it transitively, and Playwright's Node-based TS loader has no Vite `define` step, so
// `import.meta.env` is `undefined` there — fall back to `process.env`/`{}` so the import doesn't
// crash outside a real Vite context. Typed as `ImportMetaEnv` (not `Record<string, string |
// undefined>`) to match Vite's own ambient typing exactly — that type's arbitrary keys are `any`,
// which is what every direct `import.meta.env.VITE_X` access below relied on before this change.
const env = (import.meta.env ?? (typeof process !== 'undefined' ? process.env : {})) as ImportMetaEnv

const getRegionVariables = () => {
   const region = typeof window !== 'undefined' ? extractUrlRegion(window.location).region : 'au'
   const regionVariables = !!region ? Object.entries(env).filter(([key]) => key.endsWith(`_${region.toUpperCase()}`)).reduce((acc, [key, value]) => {
      const newKey = key?.split('_') || []
      newKey.pop()
      acc[newKey.join('_')] = value
      return acc
   }, {} as any) : {}

   const userPoolDomainName = regionVariables.VITE_AUTH_USERPOOLDOMAINNAME || env.VITE_USER_POOL_DOMAIN
   const userPoolDomain = `${userPoolDomainName}.auth.${domainRegionMap[region]}.amazoncognito.com`

   return {
      userPoolId: regionVariables.VITE_AUTH_USERPOOLID || env.VITE_USER_POOL_ID as string,
      userPoolDomain,
      identityPoolId: regionVariables.VITE_AUTH_USERIDENTITYPOOLCLIENTID || env.VITE_IDENTITY_POOL_ID as string,
      appUserPoolClientId: regionVariables.VITE_AUTH_USERPOOLCLIENTID || env.VITE_USER_POOL_CLIENT_ID as string,
      // v3 (infra-v3 SiteStack) publishes the squished VITE_AUTH_BACKENDUSERPOOLCLIENTID. Nothing emits
      // the legacy underscored name any more, so the second term is dead weight retained on purpose:
      // when this id resolves undefined the WS authorizer rejects the guest connection and uploads
      // fail as "file is invalid", so it is dropped only alongside the rest of the v2 reference cleanup.
      backendUserPoolClientId: regionVariables.VITE_AUTH_BACKENDUSERPOOLCLIENTID || regionVariables.VITE_AUTH_BACKEND_USER_POOL_CLIENT_ID || env.VITE_BACKEND_USER_POOL_CLIENT_ID as string,
      apiUrl: regionVariables.VITE_API_URL || env.VITE_API_URL as string,
      webSocketUrl: regionVariables.VITE_WS_URL || env.VITE_GAP_WEBSOCKET_URL as string,
      region: domainRegionMap[region] as string,
      regionBaseUrl: `${env.VITE_APP_HOST}/${region}`,
      transcribeIdentityPoolId: (regionVariables.VITE_TRANSCRIBE_IDENTITY_POOL_ID || env.VITE_TRANSCRIBE_IDENTITY_POOL_ID || '') as string,
   }
}

const variables = {
   cognitoOauthScopes: env.VITE_COGNITO_OAUTH_SCOPES?.split(',') || [],
   stage: env.VITE_STAGE as string,
   gapOrgId: '00gap', //env.VITE_GAP_ORG_ID as string,
   amplifyApiName: 'back-end',
   gapDomain: 'thegaphq.com',
   devTeam: ['kevin', 'guus', 'jason'],
   productTeam: ['jeremy', 'sarah', 'chris'],
   gapName: namingConfig.product.singular,
   gapAI: 'Gap AI',
   gapSupportEmail: 'support@app.thegaphq.com',
   defaultTimeZone: env.VITE_DEFAULT_TIME_ZONE as string,
   workspaceBackgroundColor: '#f7f6f7',
   enableRouteLogs: env.VITE_ENABLE_ROUTE_LOGS as string,
   gapCryptoKey: env.GAP_CRYPTO_KEY as string,
   advisorWelcomeVideoUrl: env.VITE_ADVISOR_WELCOME_VIDEO_URL as VimeoUrl | undefined,
   meetingTourVideoUrl: env.VITE_MEETING_TOUR_VIDEO_URL as VimeoUrl | undefined,
   contentPadding: '24px 64px 24px 64px',
   contentPaddingY: '24',
   contentPaddingX: '64',
   appBarHeight: '64',
   trialBarHeight: '40',
   gapIdTokenLocalStorageKey: 'gap:idToken',
   gapAccessTokenLocalStorageKey: 'gap:accessToken',
   gapRefreshTokenLocalStorageKey: 'gap:refreshToken',
   tokenRefreshInterval: 1000 * 60 * 10, // 10 minutes,
   allowPreworkDocEditing: env.VITE_ALLOW_PREWORK_DOC_EDITING,
   pricingUrl: 'https://thegaphq.com/pricing',
   quickFillPassword: env.VITE_QUICK_FILL_PASSWORD,
   useQuickFill: env.VITE_USE_QUICK_FILL === 'true',
   // Test-autofill is active only when the build flag is on AND we're not on a prod-like stage.
   // Centralised here so the four autofill call sites don't each re-derive the stage check.
   get quickFillActive(): boolean { return this.useQuickFill && !isProdLikeStage(this.stage) },
   isMultiRegion: !!(env.VITE_MULTI_REGION && env.VITE_MULTI_REGION !== '0'),
   chargebeeSite: (env.VITE_CHARGEBEE_SITE as string) || '',
   chargebeePublishableKey: (env.VITE_CHARGEBEE_PUBLISHABLE_KEY as string) || '',
   ...getRegionVariables()
}

export const UserOnboardingDefaults: UserDto.Onboarding = {
   hasViewedWelcome: false,
   hasViewedTacticAnnouncement: false,
   showCheckList: true,
   hasSeenMeetingGuidedTour: false,
   hasConsentedToTranscription: false,
   completedSteps: [],
   gettingStartedSteps: ['setupAccount'],
   gettingStartedSeenSteps: [],
   gettingStartedCollapsed: false,
   gettingStartedCompletedAt: undefined,
}

export default variables
